import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  OBJECT_DETECTION_ICONS,
  AUDIO_DETECTION_ICONS,
  DEFAULT_OBJECT_ICON,
  DEFAULT_AUDIO_ICON,
} from '@/config/detectionIcons'

/**
 * Shared detection chip — used in EventsPage, EventDetailModal, and notification cards.
 * Single source of truth for detection class display.
 */
export function DetectionChip({
  cls,
  confidence,
  type,
}: {
  cls: string
  confidence?: number
  type: 'object' | 'audio'
}) {
  const { t } = useTranslation('common')
  const config =
    type === 'object'
      ? (OBJECT_DETECTION_ICONS[cls] ?? DEFAULT_OBJECT_ICON)
      : (AUDIO_DETECTION_ICONS[cls] ?? DEFAULT_AUDIO_ICON)

  const nsKey = type === 'object' ? 'detectionClasses' : 'audioClasses'
  const label = t(`${nsKey}.${cls}`, { defaultValue: config.label || cls.replace(/_/g, ' ') })
  const confidencePct = confidence !== undefined ? `${Math.round(confidence * 100)}%` : null

  const Icon = config.icon
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border cursor-default',
        'bg-muted border-border/60 h-7 px-2.5',
        'hover:bg-accent hover:border-border transition-colors duration-150',
      )}
    >
      <Icon className={cn('h-4 w-4 flex-shrink-0', config.color)} weight="duotone" />
      <span className="text-xs font-medium text-foreground/75 whitespace-nowrap leading-none">
        {label}
      </span>
      {confidencePct && (
        <span className="text-[11px] font-semibold text-foreground/55 tabular-nums leading-none">
          {confidencePct}
        </span>
      )}
    </span>
  )
}
